import { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { apiRequest, CITIES, SERVICES_LIST } from '../lib/utils';
import { toast } from 'sonner';
import AppointmentModal from '../components/AppointmentModal';

export default function ContactPage() {
  const [showAppointment, setShowAppointment] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    service_needed: '',
    message: '',
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await apiRequest('/public/contact', {
        method: 'POST',
        body: JSON.stringify(formData),
      });
      setSubmitted(true);
      toast.success('Message sent! We\'ll be in touch soon.');
    } catch (error) {
      toast.error('Failed to send message. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-24 lg:pt-32" data-testid="contact-page">
      {/* Hero */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Contact Us
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto">
            Ready to restore your smile? Get in touch to schedule a consultation or ask any questions.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 lg:py-24 bg-white" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Form */}
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
                Send Us a Message
              </h2>

              {submitted ? (
                <div className="bg-green-50 rounded-2xl p-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-green-900">Message Sent!</h3>
                  <p className="mt-2 text-green-700">
                    Thank you for reaching out. We'll get back to you as soon as possible.
                  </p>
                  <Button
                    onClick={() => setShowAppointment(true)}
                    className="mt-6 bg-orange-600 hover:bg-orange-700 rounded-full"
                  >
                    Schedule an Appointment
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6 contact-form">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        required
                        value={formData.name}
                        onChange={(e) => updateField('name', e.target.value)}
                        className="mt-1"
                        placeholder="John Smith"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => updateField('email', e.target.value)}
                        className="mt-1"
                        placeholder="john@example.com"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="phone">Phone *</Label>
                      <Input
                        id="phone"
                        required
                        value={formData.phone}
                        onChange={(e) => updateField('phone', e.target.value)}
                        className="mt-1"
                        placeholder="(541) 756-2121"
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="city">City</Label>
                      <Select value={formData.city} onValueChange={(val) => updateField('city', val)}>
                        <SelectTrigger className="mt-1" data-testid="contact-city-select">
                          <SelectValue placeholder="Select your city" />
                        </SelectTrigger>
                        <SelectContent>
                          {CITIES.map((city) => (
                            <SelectItem key={city} value={city}>{city}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="service">Service Interested In</Label>
                    <Select value={formData.service_needed} onValueChange={(val) => updateField('service_needed', val)}>
                      <SelectTrigger className="mt-1" data-testid="contact-service-select">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICES_LIST.map((service) => (
                          <SelectItem key={service} value={service}>{service}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      required
                      value={formData.message}
                      onChange={(e) => updateField('message', e.target.value)}
                      className="mt-1"
                      placeholder="Tell us how we can help you..."
                      rows={5}
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full sm:w-auto bg-orange-600 hover:bg-orange-700 rounded-full px-8 py-6"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>

            {/* Contact Info & Map */}
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
                Visit Our Office
              </h2>

              {/* Contact Cards */}
              <div className="space-y-6 mb-10">
                {/* Phone - Clickable to call */}
                <a 
                  href="tel:+15417562121" 
                  className="flex items-start gap-4 p-5 bg-slate-50 rounded-xl hover:bg-orange-50 transition-colors cursor-pointer group"
                  data-testid="contact-phone-link"
                >
                  <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 group-hover:bg-orange-200 transition-colors">
                    <Phone className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Call Us</h3>
                    <p className="text-slate-600 group-hover:text-orange-600 transition-colors">
                      (541) 756-2121
                    </p>
                    <p className="text-xs text-slate-500 mt-1">Tap to call</p>
                  </div>
                </a>

                {/* Email - Not clickable, encourage scheduling */}
                <div className="flex items-start gap-4 p-5 bg-slate-50 rounded-xl">
                  <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Email</h3>
                    <p className="text-slate-600">dentureking@gmail.com</p>
                    <p className="text-xs text-orange-600 mt-1 font-medium">
                      For fastest response, schedule a consultation below
                    </p>
                  </div>
                </div>

                {/* Address - Clickable for Google Maps directions */}
                <a 
                  href="https://www.google.com/maps/dir/?api=1&destination=2495+Newmark+St,+North+Bend,+OR+97459"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 p-5 bg-slate-50 rounded-xl hover:bg-orange-50 transition-colors cursor-pointer group"
                  data-testid="contact-directions-link"
                >
                  <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 group-hover:bg-orange-200 transition-colors">
                    <MapPin className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Get Directions</h3>
                    <p className="text-slate-600 group-hover:text-orange-600 transition-colors">
                      2495 Newmark St<br />
                      North Bend, OR 97459
                    </p>
                    <p className="text-xs text-slate-500 mt-1">Tap for directions</p>
                  </div>
                </a>

                {/* Hours - Not clickable */}
                <div className="flex items-start gap-4 p-5 bg-slate-50 rounded-xl">
                  <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Hours</h3>
                    <p className="text-slate-600">
                      Mon, Tue, Thu: 9:30 AM - 4:30 PM<br />
                      Wed, Fri: 9:00 AM - 3:00 PM<br />
                      Sat - Sun: Closed
                    </p>
                  </div>
                </div>
              </div>

              {/* Google Maps Embed */}
              <div className="rounded-xl overflow-hidden shadow-md" data-testid="contact-map">
                <iframe
                  src="https://maps.google.com/maps?q=2495+Newmark+St,+North+Bend,+OR+97459&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="350"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Community Dental Lab Location"
                ></iframe>
              </div>

              {/* Get Directions Button */}
              <div className="mt-4 text-center">
                <a 
                  href="https://www.google.com/maps/dir/?api=1&destination=2495+Newmark+St,+North+Bend,+OR+97459"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-orange-600 hover:text-orange-700 font-medium"
                >
                  <MapPin className="w-4 h-4" />
                  Open in Google Maps
                </a>
              </div>

              {/* Schedule Button */}
              <div className="mt-8 text-center">
                <Button
                  onClick={() => setShowAppointment(true)}
                  className="bg-slate-900 hover:bg-slate-800 rounded-full px-8 py-6"
                  data-testid="contact-schedule-btn"
                >
                  Schedule an Appointment
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <AppointmentModal open={showAppointment} onOpenChange={setShowAppointment} />
    </div>
  );
}
